import { Fragment, PropsWithChildren, ReactNode, useMemo } from 'react'

import { Schema, SchemaKey } from '@formily/json-schema'
import {
  observer,
  RecursionField,
  useField,
  useFieldSchema
} from '@formily/react'
import { markRaw, model } from '@formily/reactive'
import { toArr } from '@formily/shared'
import { useMemoizedFn } from 'ahooks'
import { Badge, Collapse, CollapsePanelProps } from 'antd'
import { createStyles } from 'antd-style'

import { FormBlockProps, FormBlockStore } from './interface'

const useStyles = createStyles(
  ({ prefixCls, token }, { headerBordered }: { headerBordered?: boolean }) => ({
    formBlock: {
      [`&.${prefixCls}-collapse`]: {
        backgroundColor: 'transparent',
        [`&>.${prefixCls}-collapse-item`]: {
          border: 'none',
          [`&>.${prefixCls}-collapse-header`]: {
            paddingBlock: token.paddingXS,
            paddingInline: token.paddingSM,
            marginBlockEnd: token.marginMD,
            fontWeight: token.fontWeightStrong,
            backgroundColor: headerBordered ? undefined : '#EEEEEE',
            borderBottom: headerBordered
              ? `${token.lineWidth}px ${token.lineType} ${token.colorBorder}`
              : undefined
          }
        }
      }
    }
  })
)

const FORM_BLOCK_PANEL_NAME = 'FormBlockPanel'

const usePanels = () => {
  const collapseField = useField()
  const schema = useFieldSchema()
  return useMemo(() => {
    const panels: { name: SchemaKey; props: any; schema: Schema }[] = []
    schema.mapProperties((schema, name) => {
      const field = collapseField
        .query(collapseField.address.concat(name))
        .take()
      if (field?.display === 'none' || field?.display === 'hidden') return
      if (schema['x-component']?.displayName === FORM_BLOCK_PANEL_NAME) {
        panels.push({
          name,
          props: {
            header: schema.title,
            ...schema?.['x-component-props'],
            key: schema?.['x-component-props']?.key || name
          },
          schema
        })
      }
    })
    return panels
  }, [collapseField, schema])
}

const createFormBlockStore = (defaultActiveKeys?: string[]): FormBlockStore => {
  const store = model({
    activeKeys: defaultActiveKeys ?? [],
    setActiveKeys(keys: string[]) {
      store.activeKeys = keys
    },
    hasActiveKey(key: string) {
      if (Array.isArray(store.activeKeys)) {
        if (store.activeKeys.includes(key)) {
          return true
        }
      } else if (store.activeKeys == key) {
        return true
      }
      return false
    },
    addActiveKey(key: string) {
      if (store.hasActiveKey(key)) return
      store.activeKeys = toArr(store.activeKeys).concat(key)
    },
    removeActiveKey(key: string) {
      if (Array.isArray(store.activeKeys)) {
        store.activeKeys = store.activeKeys.filter(item => item != key)
      } else {
        store.activeKeys = []
      }
    },
    toggleActiveKey(key: string) {
      if (store.hasActiveKey(key)) {
        store.removeActiveKey(key)
      } else {
        store.addActiveKey(key)
      }
    }
  })
  return markRaw(store)
}

const InternalFormCollapse = observer(
  ({
    className,
    style,
    accordion,
    collapsible,
    defaultActiveKey,
    activeKey,
    headerBordered,
    store: externalStore,
    onChange
  }: FormBlockProps) => {
    const field = useField()
    const panels = usePanels()
    const store = useMemo(() => {
      return (
        externalStore ??
        createFormBlockStore(
          toArr(defaultActiveKey ?? panels.map(item => item.name))
        )
      )
    }, [panels])

    const takeActiveKeys = () => {
      if (activeKey) return activeKey
      if (store.activeKeys) return store?.activeKeys
      if (accordion) return panels[0]?.name
      return panels.map(item => item.name)
    }

    const badgedHeader = useMemoizedFn((key: SchemaKey, header: ReactNode) => {
      const errors = field.form.queryFeedbacks({
        type: 'error',
        address: `${field.address.concat(key)}.*`
      })
      if (collapsible && errors.length) {
        return (
          <Badge className='errors-badge' count={errors.length} size='small'>
            {header}
          </Badge>
        )
      }
      return header
    })

    const { cx, styles } = useStyles({ headerBordered })

    const panelItems = useMemo(
      () =>
        panels.map(({ props, schema, name }, index) => ({
          key: index,
          label: badgedHeader(name, props.header),
          forceRender: true,
          showArrow: !!collapsible,
          ...props,
          children: <RecursionField name={name} schema={schema} />
        })),
      [panels, badgedHeader, collapsible]
    )

    return (
      <Collapse
        accordion={accordion}
        activeKey={takeActiveKeys()}
        bordered={false}
        className={cx(styles.formBlock, className)}
        collapsible={collapsible ? 'header' : undefined}
        defaultActiveKey={defaultActiveKey}
        expandIconPlacement='end'
        items={panelItems}
        style={style}
        onChange={key => {
          onChange?.(key)
          store.setActiveKeys?.(toArr(key))
        }}
      />
    )
  }
)

const FormBlockPanel = ({
  children
}: PropsWithChildren<CollapsePanelProps>) => {
  return <Fragment>{children}</Fragment>
}

FormBlockPanel.displayName = FORM_BLOCK_PANEL_NAME

export const FormBlock = Object.assign(InternalFormCollapse, {
  Panel: FormBlockPanel,
  createStore: createFormBlockStore
})
